// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { getAuthorizations, createAuthorization, cancelAuthorization, getBanks, processATMWithdrawal } from "../lib/api";
import { toast } from "sonner";
import { Plus, KeyRound, Search, XCircle, Copy, Banknote, Phone } from "lucide-react";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const StatusBadge = ({ status }) => {
  const statusStyles = {
    initiated: "status-info",
    authorized: "status-info",
    redeemed: "status-success",
    expired: "status-neutral",
    cancelled: "status-neutral",
    blocked: "status-error"
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[status] || 'status-neutral'}`}>
      {status}
    </span>
  );
};

const Authorizations = () => {
  const [authorizations, setAuthorizations] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false);
  const [createdAuth, setCreatedAuth] = useState(null);
  const [selectedAuth, setSelectedAuth] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    issuer_bank_code: "",
    wallet_reference: "",
    requested_amount: "",
    customer_reference: ""
  });

  const [withdrawForm, setWithdrawForm] = useState({
    authorization_code: "",
    pin: "",
    acquirer_bank_code: "",
    atm_id: "",
    dispensed_amount: ""
  });

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatus !== "all") {
        params.status = filterStatus;
      }
      const [authRes, banksRes] = await Promise.all([
        getAuthorizations(params),
        getBanks({ is_active: true })
      ]);
      setAuthorizations(authRes.data);
      setBanks(banksRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const issuerBanks = banks.filter(b => b.bank_type === "issuer" || b.bank_type === "both");
  const acquirerBanks = banks.filter(b => b.bank_type === "acquirer" || b.bank_type === "both");
  
  // Get selected issuer bank format profile
  const selectedIssuerBank = banks.find(b => b.bank_code === createForm.issuer_bank_code);
  const isMSISDNBank = selectedIssuerBank?.format_profile?.reference_type === "msisdn";

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await createAuthorization({
        ...createForm,
        requested_amount: parseFloat(createForm.requested_amount)
      });
      setCreatedAuth(res.data);
      toast.success("Authorization created successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create authorization");
    }
  };

  const handleCancel = async (authId) => {
    if (!window.confirm("Are you sure you want to cancel this authorization?")) {
      return;
    }
    try {
      await cancelAuthorization(authId);
      toast.success("Authorization cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel authorization");
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    try {
      const res = await processATMWithdrawal({
        ...withdrawForm,
        dispensed_amount: parseFloat(withdrawForm.dispensed_amount)
      });
      toast.success(`Withdrawal successful! Transaction ID: ${res.data.transaction_id}`);
      setIsWithdrawDialogOpen(false);
      setWithdrawForm({
        authorization_code: "",
        pin: "",
        acquirer_bank_code: "",
        atm_id: "",
        dispensed_amount: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const openWithdrawDialog = (auth) => {
    setSelectedAuth(auth);
    setWithdrawForm({
      authorization_code: auth.authorization_code,
      pin: "",
      acquirer_bank_code: "",
      atm_id: "ATM-001",
      dispensed_amount: auth.requested_amount.toString()
    });
    setIsWithdrawDialogOpen(true);
  };

  const filteredAuths = authorizations.filter(auth => 
    auth.authorization_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    auth.wallet_reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
    auth.issuer_bank_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="authorizations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Authorizations</h1>
          <p className="text-muted-foreground">Manage withdrawal authorization tokens</p>
        </div>
        
        <div className="flex gap-2">
          {/* Simulate ATM Withdrawal */}
          <Dialog open={isWithdrawDialogOpen} onOpenChange={setIsWithdrawDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="simulate-atm-btn">
                <Banknote className="h-4 w-4 mr-2" />
                Simulate ATM
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Simulate ATM Withdrawal</DialogTitle>
                <DialogDescription>
                  Test the ATM withdrawal flow with an existing authorization
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleWithdraw} className="space-y-4">
                <div className="space-y-2">
                  <Label>Authorization Code</Label>
                  <Input
                    placeholder="Enter or select auth code"
                    value={withdrawForm.authorization_code}
                    onChange={(e) => setWithdrawForm({ ...withdrawForm, authorization_code: e.target.value })}
                    required
                    className="font-mono"
                    data-testid="withdraw-auth-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>PIN</Label>
                  <Input
                    type="password"
                    placeholder="Enter PIN"
                    value={withdrawForm.pin}
                    onChange={(e) => setWithdrawForm({ ...withdrawForm, pin: e.target.value })}
                    required
                    maxLength={6}
                    data-testid="withdraw-pin"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Acquirer Bank</Label>
                  <Select 
                    value={withdrawForm.acquirer_bank_code} 
                    onValueChange={(value) => setWithdrawForm({ ...withdrawForm, acquirer_bank_code: value })}
                  >
                    <SelectTrigger data-testid="withdraw-acquirer-select">
                      <SelectValue placeholder="Select ATM bank" />
                    </SelectTrigger>
                    <SelectContent>
                      {acquirerBanks.map((bank) => (
                        <SelectItem key={bank.id} value={bank.bank_code}>
                          {bank.bank_code} - {bank.bank_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ATM ID</Label>
                    <Input
                      placeholder="ATM-001"
                      value={withdrawForm.atm_id}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, atm_id: e.target.value })}
                      required
                      data-testid="withdraw-atm-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount (ZAR)</Label>
                    <Input
                      type="number"
                      placeholder="500.00"
                      value={withdrawForm.dispensed_amount}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, dispensed_amount: e.target.value })}
                      required
                      data-testid="withdraw-amount"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsWithdrawDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="process-withdraw-btn">
                    Dispense Cash
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Create Authorization */}
          <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
            setIsCreateDialogOpen(open);
            if (!open) {
              setCreatedAuth(null);
              setCreateForm({
                issuer_bank_code: "",
                wallet_reference: "",
                requested_amount: "",
                customer_reference: ""
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-testid="create-auth-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Authorization
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{createdAuth ? "Authorization Created" : "Create Withdrawal Authorization"}</DialogTitle>
                <DialogDescription>
                  {createdAuth 
                    ? "Save these credentials - PIN is shown only once!" 
                    : "Generate a new withdrawal authorization token"
                  }
                </DialogDescription>
              </DialogHeader>
              
              {createdAuth ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Authorization Code</Label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 font-mono text-lg bg-secondary px-3 py-2 rounded">
                          {createdAuth.authorization_code}
                        </code>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(createdAuth.authorization_code)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      {isMSISDNBank && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          This is your mobile number (MSISDN format)
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">PIN (Save this!)</Label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 font-mono text-2xl font-bold text-green-400 bg-secondary px-3 py-2 rounded">
                          {createdAuth.pin}
                        </code>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(createdAuth.pin)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <Label className="text-xs text-muted-foreground">Amount</Label>
                        <p className="font-mono">{formatCurrency(createdAuth.requested_amount)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Expires</Label>
                        <p className="font-mono text-yellow-400">{formatDate(createdAuth.expiry)}</p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={() => {
                      setIsCreateDialogOpen(false);
                      setCreatedAuth(null);
                    }}
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Issuer Bank</Label>
                    <Select 
                      value={createForm.issuer_bank_code} 
                      onValueChange={(value) => setCreateForm({ ...createForm, issuer_bank_code: value })}
                    >
                      <SelectTrigger data-testid="issuer-bank-select">
                        <SelectValue placeholder="Select issuing bank" />
                      </SelectTrigger>
                      <SelectContent>
                        {issuerBanks.map((bank) => (
                          <SelectItem key={bank.id} value={bank.bank_code}>
                            {bank.bank_code} - {bank.bank_name}
                            {bank.format_profile?.reference_type === "msisdn" && " (MSISDN)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>
                      {isMSISDNBank ? "Mobile Number (MSISDN)" : "Wallet Reference"}
                    </Label>
                    <Input
                      placeholder={isMSISDNBank ? "+27821234567" : "Wallet ID or reference"}
                      value={createForm.wallet_reference}
                      onChange={(e) => setCreateForm({ ...createForm, wallet_reference: e.target.value })}
                      required
                      data-testid="wallet-ref-input"
                    />
                    {isMSISDNBank && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        For FNB, the mobile number will be your authorization code at the ATM
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Amount (ZAR)</Label>
                    <Input
                      type="number"
                      placeholder="1000.00"
                      value={createForm.requested_amount}
                      onChange={(e) => setCreateForm({ ...createForm, requested_amount: e.target.value })}
                      required
                      min="1"
                      step="0.01"
                      data-testid="amount-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Customer Reference (Optional)</Label>
                    <Input
                      placeholder="Reference note"
                      value={createForm.customer_reference}
                      onChange={(e) => setCreateForm({ ...createForm, customer_reference: e.target.value })}
                      data-testid="customer-ref-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-auth-btn">
                      Generate Token
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by code, wallet reference..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-auth-input"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="initiated">Initiated</SelectItem>
                <SelectItem value="authorized">Authorized</SelectItem>
                <SelectItem value="redeemed">Redeemed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Authorizations Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Auth Code</TableHead>
                <TableHead>Issuer</TableHead>
                <TableHead>Wallet Ref</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading authorizations...</div>
                  </TableCell>
                </TableRow>
              ) : filteredAuths.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <KeyRound className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No authorizations found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAuths.map((auth) => (
                  <TableRow key={auth.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm bg-secondary px-2 py-1 rounded">
                          {auth.authorization_code.length > 12 
                            ? `${auth.authorization_code.slice(0, 12)}...` 
                            : auth.authorization_code}
                        </code>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(auth.authorization_code)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{auth.issuer_bank_code}</span>
                    </TableCell>
                    <TableCell>
                      <code className="font-mono text-xs text-muted-foreground">
                        {auth.wallet_reference.length > 15 
                          ? `${auth.wallet_reference.slice(0, 15)}...` 
                          : auth.wallet_reference}
                      </code>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono">{formatCurrency(auth.requested_amount)}</span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={auth.status} />
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(auth.expiry)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`font-mono ${auth.attempt_count >= 3 ? 'text-red-400' : ''}`}>
                        {auth.attempt_count}/3
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {["initiated", "authorized"].includes(auth.status) && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openWithdrawDialog(auth)}
                              data-testid={`withdraw-${auth.id}`}
                            >
                              <Banknote className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleCancel(auth.id)}
                              data-testid={`cancel-${auth.id}`}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Authorizations;
